<template>
  <transition name="modal" mode="out-in" appear>
    <div v-if="isOpen" class="modal" tabindex="-1" role="dialog" ref="modal" @click="handleOutsideClick">
      <div class="modal-dialog" :class="size + '-modal'" role="document">
        <div :is="modalContentElement" class="modal-content">
          <div v-if="showHeader" class="modal-header">
            <button type="button" class="close" aria-label="Close" @click="closeModal"><span aria-hidden="true">&times;</span></button>
            <slot name="header">
              <h4 class="modal-title">{{ title }}</h4>
            </slot>
          </div>

          <div class="modal-body" :data-scrollable="bodyScroll">
            <slot name="body"></slot>
          </div>
          <div v-if="showFooter" class="modal-footer">
            <slot name="footer">
              <button v-if="showSuccessBtn" type="submit" class="btn btn-success" @click="handleSave">{{ btnSuccessText }}</button>
              <button type="button" class="btn btn-secondary" @click="closeModal">{{ btnCancelText }}</button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'EnModal',
  props: {
    /**
    * Sets the title in the modal header.
    */
    title: {
      type: String,
      default: ''
    },
    /**
    * The width of the modal.
    * @values xs, sm, md, lg, xl, full
    */
    size: {
      type: String,
      default: 'md',
    },
    /**
    *  Renders the modal footer and footer slot.
    */
    showFooter: {
      type: Boolean,
      default: true,
    },
    /**
    *  Renders the modal header and header slot.
    */
    showHeader: {
      type: Boolean,
      default: true,
    },
    /**
    *  Shows the success button in the footer.
    */
    showSuccessBtn: {
      type: Boolean,
      default: true,
    },
    /**
    * Allows the modal to be closed using the close buttons.
    */
    canClose: {
      type: Boolean,
      default: true,
    },
    /**
     * Displays the modal. Should be controlled outside of the modal component.
     */
    showModal: {
      type: Boolean,
      default: false,
    },
    /**
    * Prevents modal from closing when clicking outside of the modal.
    */
    closeOnClickOutside: {
      type: Boolean,
      default: true,
    },
    /**
    * Changes the container element of the modal content.
    */
    modalContentElement: {
      type: String,
      default: 'div'
    },
    /**
    * Sets text for the success button.
    */
    btnSuccessText: {
      type: String,
      default: 'Save'
    },
    /**
    * Sets text for the cancel button.
    */
    btnCancelText: {
      type: String,
      default: 'Cancel'
    },
    /**
    * Applies a scrollbar to the body of the modal when the content gets too long.
    */
    bodyScroll: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      body: document.getElementsByTagName('body')[0]
    }
  },
  methods: {
    closeModal() {
      if (this.canClose) {
        this.isOpen = false;
        this.body.style.overflow = '';
        /**
        * Close event.
        */
        this.$emit('close');
        document.removeEventListener('keyup', this.handleKeypress)
      }
    },
    openModal() {
      this.isOpen = true;
      this.body.style.overflow = 'hidden';
      /**
      * Open event.
      */
      this.$emit('open');
      document.addEventListener('keyup', this.handleKeypress)
    },
    handleOutsideClick(e) {
      // e.stopPropagation();
      const target = e.target
      if (target === this.$refs.modal && this.closeOnClickOutside) {
        this.closeModal();
      }
    },
    handleSave() {
      /**
      * This is triggered with the default save button.
      * Emits save event with the close modal method as an arg.
      */
      this.$emit('save', this.closeModal);
    },
    handleKeypress(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        this.closeModal()
      }
    }
  },
  destroyed() {
    document.removeEventListener('keyup', this.handleKeypress)
  },
  watch: {
    showModal: {
      immediate: true,
      handler(newVal) {
        if (newVal === true) {
          this.openModal();
        } else {
          this.closeModal();
        }
      },
    },
  },

}
</script>

<style lang="scss" scoped>
.modal {
  display: block;
  padding: 10px;
  opacity: 1;
  top: 0;
  transition: .2s ease-in-out;
  overflow: auto;
  background: rgba(#000, .2);

  @include from(sm) {
    display: block;
    padding: 50px
  }

  &-enter-active, &-leave-active {

    .modal-content {
      transition: .2s ease-in-out;
      transform: scale(1);
    }
  }

  &-enter, &-leave-to {
    transition: .2s ease-in-out;
    opacity: 0;

    .modal-content {
      transition: .2s ease-in-out;
      transform: scale(1.1);
    }
  }


  &-dialog {
      width: 100%;
      margin: 30px auto;

    @include from(sm) {
      width: 100%;

      &.xs-modal {
        max-width: 300px;
      }

      &.sm-modal {
        max-width: 450px;
      }

      &.md-modal {
        max-width: 550px;
      }

      &.lg-modal {
        max-width: 750px;
      }

      &.xl-modal {
        max-width: 1100px;
      }

      &.full-modal {
        max-width: 100%;
      }
    }
  }

  &-body {
    max-height: none;
    overflow: unset;

    &[data-scrollable='true'] {
      max-height: 550px;
      overflow-y: auto;
    }
  }

  &-footer {
    .btn {
      margin-bottom: 0;
    }
  }
}

</style>
